import type { ReactNode } from "react";
import { CircleCheck, CircleX, Info, TriangleAlert, type LucideIcon } from "lucide-react";

import { Icon } from "./Icon";
import type { Tone } from "./StatusPill";

/**
 * A message the page says to its reader, in one of four tones (there is no
 * neutral notice: a neutral message is a plain card). The tone picks the colour
 * AND the icon, so the meaning never rests on the hue alone.
 *
 *   info     a fact worth knowing            (the environment is claimed at first sign-in)
 *   success  it worked                       (saved, sent, ready)
 *   warning  read this before you go on      (this locks out appliances, a value is missing)
 *   danger   it failed                       (a refused action, a load that timed out)
 *
 * `title` is the bold first line; the children are the sentence, or anything a
 * message needs (a `<pre>` of raw output, a `<details>`). `actions` sit on the
 * right of the box (a retry).
 *
 * Announcing it is the CALLER's call, because only the caller knows whether the
 * notice was there at load or appeared after a click: `role="alert"` for an error
 * that just happened (a refused action, a failed save), `role="status"` for a
 * result or a notice that appears while the page is open, none for a notice that
 * simply sits on the page.
 *
 * `className` is for LAYOUT only (a margin): the tone owns the colours.
 */
export type NoticeTone = Exclude<Tone, "neutral">;

// Literal tables, not `border-${tone}-edge`: Tailwind finds a class by reading its full text in
// the source, and a computed one is never generated.
//
// The surface alone — the border's colour and the soft fill, no text colour, no shape — for the
// few things that are tinted by their state and are NOT a message: the header of a destructive
// dialog, the card of a run in progress or failed, the chip of an orphan reference. The caller
// draws the shape (`rounded`, `border`, padding) and its own text colours; a page never picks
// a tone's colours itself. Exported for the same reason as `TONE_FILL`.
export const TONE_SURFACE = {
  info: "border-info-edge bg-info-soft",
  success: "border-success-edge bg-success-soft",
  warning: "border-warning-edge bg-warning-soft",
  danger: "border-danger-edge bg-danger-soft",
} as const satisfies Record<NoticeTone, string>;

// A field of the page's own surface inset in a tinted box, or beside it — a `<pre>` of raw output —
// edged and lettered in the tone: the text is `fg` (>= 5.4:1 on white).
export const TONE_INSET = {
  info: "border-info-edge bg-surface text-info-fg",
  success: "border-success-edge bg-surface text-success-fg",
  warning: "border-warning-edge bg-surface text-warning-fg",
  danger: "border-danger-edge bg-surface text-danger-fg",
} as const satisfies Record<NoticeTone, string>;

const BOX = {
  info: `${TONE_SURFACE.info} text-info-fg`,
  success: `${TONE_SURFACE.success} text-success-fg`,
  warning: `${TONE_SURFACE.warning} text-warning-fg`,
  danger: `${TONE_SURFACE.danger} text-danger-fg`,
} as const satisfies Record<NoticeTone, string>;

const GLYPH = {
  info: Info,
  success: CircleCheck,
  warning: TriangleAlert,
  danger: CircleX,
} as const satisfies Record<NoticeTone, LucideIcon>;

export function Notice({
  tone,
  title,
  actions,
  role,
  className = "",
  children,
}: {
  tone: NoticeTone;
  title?: ReactNode;
  actions?: ReactNode;
  role?: "alert" | "status";
  className?: string;
  children?: ReactNode;
}) {
  return (
    <div
      role={role}
      className={`flex items-start gap-2.5 rounded-md border px-3.5 py-3 text-sm ${BOX[tone]}${className ? ` ${className}` : ""}`}
    >
      <Icon icon={GLYPH[tone]} className="mt-0.5" />
      <div className="min-w-0 flex-1 break-words">
        {title && <p className="font-semibold">{title}</p>}
        {children}
      </div>
      {actions && <div className="shrink-0">{actions}</div>}
    </div>
  );
}
